import React, { useEffect } from 'react';
import {
  X,
  BookOpen,
  Compass,
  Layers,
  ShieldAlert,
  Cpu,
  CheckCircle2,
  ArrowRight,
  BarChart3,
  Target,
} from 'lucide-react';

interface CheatSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CheatSheetModal: React.FC<CheatSheetModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="cheat-sheet-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cheat-sheet-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 bg-black/75 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-xl bg-[#0c111d] border border-[#1e293b] shadow-2xl text-slate-200 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Sticky Header */}
        <div className="sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6 py-3.5 bg-[#0b0f17]/95 border-b border-[#182232] backdrop-blur">
          <div className="flex items-center gap-2.5">
            <BookOpen className="w-5 h-5 text-terminal-cyan shrink-0" />
            <div>
              <h2
                id="cheat-sheet-title"
                className="text-sm sm:text-base font-bold text-slate-100 flex items-center gap-2"
              >
                <span>科斯托蘭尼雞蛋理論雙層決策系統 — 速查表</span>
                <span className="hidden sm:inline-block text-xs font-mono font-normal text-sky-400">
                  (KOSTOLANY EGG CHEAT SHEET &amp; STEP-BY-STEP GUIDE)
                </span>
              </h2>
              <p className="text-[11px] font-mono text-slate-400">
                順時針循環：底部 Y → 左側升市 (A1→A2→A3) → 頂部 X → 右側跌市 (B1→B2→B3) → 底部 Y
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="cheat-sheet-close-btn"
            onClick={onClose}
            className="p-1.5 rounded-lg bg-[#141b28] hover:bg-[#1e293b] text-slate-300 hover:text-white border border-[#263348] transition-colors cursor-pointer"
            title="關閉速查表 (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Single-Page Cheat Sheet Content Grid */}
        <div className="p-4 sm:p-6 space-y-5 text-xs leading-relaxed">
          {/* Section 1: Canonical 6 Phases & 3 Operational Zones */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-slate-100 font-bold text-xs sm:text-sm">
              <Compass className="w-4 h-4 text-terminal-cyan" />
              <span>1. 科斯托蘭尼雞蛋六階段與三區操作法則 (The 6 Phases &amp; 3 Zones)</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-mono">
              {/* Left Column: Upward Bullish Trend (Y -> X) */}
              <div className="p-3.5 rounded-lg bg-[#0f1624] border border-sky-500/30 space-y-2.5">
                <div className="flex items-center justify-between border-b border-[#1c2638] pb-1.5">
                  <span className="font-bold text-sky-300">
                    ↑ 左半部：升市循環 (Y → A1 → A2 → A3 → X)
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-blue-950/70 text-sky-300">
                    Upward Bull Arc
                  </span>
                </div>
                <div className="space-y-2 font-sans">
                  <div className="p-2 rounded bg-[#0b0f17] border border-[#182232]">
                    <div className="flex justify-between font-mono font-bold text-sky-300">
                      <span>A1 升市初段（修正階段）</span>
                      <span className="text-terminal-cyan">[買進區 Buying]</span>
                    </div>
                    <p className="text-slate-300 mt-0.5">
                      成交量小、持股者數量少（固執的投資者持有）。MA200 翻平上彎、VR60 量縮築底，為最佳右側初升買點。
                    </p>
                  </div>
                  <div className="p-2 rounded bg-[#0b0f17] border border-[#182232]">
                    <div className="flex justify-between font-mono font-bold text-amber-300">
                      <span>A2 升市中段（相隨階段）</span>
                      <span className="text-amber-300">[等待區 Waiting]</span>
                    </div>
                    <p className="text-slate-300 mt-0.5">
                      成交量與持股者數量同步增加，價量齊揚。操作原則為「吃安眠藥順勢續抱」，沿 20MA/60MA 移動停利。
                    </p>
                  </div>
                  <div className="p-2 rounded bg-[#0b0f17] border border-[#182232]">
                    <div className="flex justify-between font-mono font-bold text-emerald-400">
                      <span>A3 升市末段（過熱階段 → 頂點 X）</span>
                      <span className="text-emerald-400">[賣出區 Selling]</span>
                    </div>
                    <p className="text-slate-300 mt-0.5">
                      成交量異常活躍、全市場狂熱追價，持股者數量達到最高峰 (X)。小狗遠離主人 (MA200 正乖離 &gt; 35%)，分批獲利賣出。
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column: Downward Bearish Trend (X -> Y) */}
              <div className="p-3.5 rounded-lg bg-[#0f1624] border border-slate-600/40 space-y-2.5">
                <div className="flex items-center justify-between border-b border-[#1c2638] pb-1.5">
                  <span className="font-bold text-slate-200">
                    ↓ 右半部：跌市循環 (X → B1 → B2 → B3 → Y)
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                    Downward Bear Arc
                  </span>
                </div>
                <div className="space-y-2 font-sans">
                  <div className="p-2 rounded bg-[#0b0f17] border border-[#182232]">
                    <div className="flex justify-between font-mono font-bold text-emerald-400">
                      <span>B1 跌市初段（修正階段）</span>
                      <span className="text-emerald-400">[賣出區 Selling]</span>
                    </div>
                    <p className="text-slate-300 mt-0.5">
                      成交量減少、持股者數量緩減，股價跌破短期均線做頭。反彈應果斷出清減碼，切勿誤判為健康回檔。
                    </p>
                  </div>
                  <div className="p-2 rounded bg-[#0b0f17] border border-[#182232]">
                    <div className="flex justify-between font-mono font-bold text-amber-300">
                      <span>B2 跌市中段（相隨階段）</span>
                      <span className="text-amber-300">[等待區 Waiting]</span>
                    </div>
                    <p className="text-slate-300 mt-0.5">
                      成交量日漸增加、持股者數量持續減少，主跌段波浪展開。應保留現金場外觀望，嚴禁過早摸底。
                    </p>
                  </div>
                  <div className="p-2 rounded bg-[#0b0f17] border border-[#182232]">
                    <div className="flex justify-between font-mono font-bold text-sky-300">
                      <span>B3 跌市末段（過熱階段 → 底點 Y）</span>
                      <span className="text-terminal-cyan">[買進區 Buying]</span>
                    </div>
                    <p className="text-slate-300 mt-0.5">
                      成交量暴增（恐慌融資斷頭）、持股者數量降至最低點 (Y)。籌碼由猶豫者洗入固執者手中，為逆勢左側買點。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: How to Read the Breadth Chart & Visual Panels */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-slate-100 font-bold text-xs sm:text-sm">
              <BarChart3 className="w-4 h-4 text-terminal-cyan" />
              <span>2. 如何判讀「全市場循環深度圖 (60D / 120D / 250D)」與類股熱力圖？</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-[#0b0f17] border border-[#182232] space-y-1.5">
                <div className="font-mono font-bold text-sky-300">
                  全市場循環深度堆疊圖 (Breadth Stacked Area)
                </div>
                <p className="text-slate-300">
                  此圖追蹤台股 1,826 檔股票隨時間在三個操作區的結構演變（右上角可切換 <code className="text-sky-300">60D</code> 季線、<code className="text-sky-300">120D</code> 半年線、<code className="text-sky-300">250D</code> 年線視窗）：
                </p>
                <ul className="space-y-1 text-slate-300 list-disc list-inside">
                  <li>
                    <strong className="text-sky-300">底層藍色面積 (B3+A1 買進區)：</strong>當藍色面積由底部大幅擴張至 &gt;50%，代表全市場完成築底進入多頭牛市 (`BULL_ZONE`)。
                  </li>
                  <li>
                    <strong className="text-slate-200">中層灰色面積 (A2+B2 等待區)：</strong>趨勢行進中的標的佔比。
                  </li>
                  <li>
                    <strong className="text-emerald-300">上層綠色面積 (A3+B1 賣出區)：</strong>當綠色面積急遽擴大 (&gt;25%) 且青色加權指數曲線走平，為大盤做頭轉折警訊。
                  </li>
                </ul>
              </div>

              <div className="p-3.5 rounded-lg bg-[#0b0f17] border border-[#182232] space-y-1.5">
                <div className="font-mono font-bold text-sky-300">
                  類股板塊熱力圖 (Sector Breadth Heatmap)
                </div>
                <p className="text-slate-300">
                  將 1,826 檔股票依 TWSE/TPEx 29 個產業分類，統計各產業在 <code className="text-sky-300">B3, A1, A2, A3, B1, B2</code> 的檔數與百分比：
                </p>
                <ul className="space-y-1 text-slate-300 list-disc list-inside">
                  <li>
                    <strong className="text-slate-100">尋找主流族群：</strong>優先鎖定「買進區佔比 (B3+A1)」最高或主導階段為 <code className="text-sky-300">A1 / A2</code> 的產業（如半導體、電腦週邊）。
                  </li>
                  <li>
                    <strong className="text-slate-100">一鍵穿透篩選：</strong>直接點擊熱力圖中任一「產業 × 階段」方格，系統會自動跳轉至「個股掃描」並列出該族群符合條件的所有股票。
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section 3: Step-by-Step Practical Workflows (Opportunities, Risk Avoidance, Backtest) */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-slate-100 font-bold text-xs sm:text-sm">
              <Target className="w-4 h-4 text-terminal-cyan" />
              <span>3. 實戰標準作業流程：如何尋找買點、避開風險與執行回測？ (Step-by-Step Guide)</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Workflow A: Finding Opportunities */}
              <div className="p-3.5 rounded-lg bg-[#0b0f17] border border-sky-500/30 space-y-2">
                <div className="font-mono font-bold text-sky-300">
                  步驟 A：如何尋找高勝率投資機會？
                </div>
                <ol className="space-y-1.5 text-slate-300 list-decimal list-inside">
                  <li>
                    <strong className="text-slate-100">確認大盤綠燈：</strong>看頂部狀態列「宏觀週期」是否為 <code className="text-sky-300">A1/A2</code> 且「做多比例」&gt;50%。
                  </li>
                  <li>
                    <strong className="text-slate-100">篩選強勢板塊：</strong>點左側「類股板塊熱力」，點擊主流產業的 <code className="text-sky-300">A1</code>（初升吸籌）或 <code className="text-sky-300">B3</code>（恐慌逆勢）方格。
                  </li>
                  <li>
                    <strong className="text-slate-100">雙層共振精選：</strong>在「個股掃描」優先挑選共振訊號為 <code className="text-sky-300">STRONG_BUY_A1</code>、<code className="text-sky-300">RS20 &gt; 0</code>（強於大盤）且 <code className="text-sky-300">VR60</code> 溫和放量的標的。
                  </li>
                  <li>
                    <strong className="text-slate-100">計算進場張數：</strong>點擊該股開啟右側「深度診斷」，輸入總資金即可獲得 4G 1% 風控建議買進張數與零股數。
                  </li>
                </ol>
              </div>

              {/* Workflow B: Avoiding Risks */}
              <div className="p-3.5 rounded-lg bg-[#0b0f17] border border-emerald-500/30 space-y-2">
                <div className="font-mono font-bold text-emerald-400">
                  步驟 B：如何避開追高與破線風險？
                </div>
                <ol className="space-y-1.5 text-slate-300 list-decimal list-inside">
                  <li>
                    <strong className="text-slate-100">避開小狗遠離主人 (A3)：</strong>當個股處於 <code className="text-emerald-400">A3 升市末段</code> 且 MA200 正乖離 &gt;+35%（階梯 5），嚴禁追高並應分批獲利了結。
                  </li>
                  <li>
                    <strong className="text-slate-100">嚴格執行 B1/B2 停損：</strong>當個股轉入 <code className="text-emerald-400">B1 跌市初段</code> 或 <code className="text-amber-300">B2 跌市中段</code>，系統會將共振乘數強制歸零 (<code className="text-emerald-400">0.0x</code>)，應立即減碼退出。
                  </li>
                  <li>
                    <strong className="text-slate-100">台股微結構安全警示：</strong>掃描表標註 <code className="text-rose-400">處置股</code>、<code className="text-amber-300">注意股</code> 或 <code className="text-rose-400">漲跌停鎖死</code> 之標的，應避開流動性枯竭風險。
                  </li>
                </ol>
              </div>

              {/* Workflow C: Running Backtest & Tuning */}
              <div className="p-3.5 rounded-lg bg-[#0b0f17] border border-amber-500/30 space-y-2">
                <div className="font-mono font-bold text-amber-300">
                  步驟 C：如何執行回測與參數調校？
                </div>
                <ol className="space-y-1.5 text-slate-300 list-decimal list-inside">
                  <li>
                    <strong className="text-slate-100">快速切換預設組：</strong>使用右上角選單切換 <code className="text-sky-300">Default v1</code>、<code className="text-sky-300">Bull Momentum</code> 或 <code className="text-sky-300">Defensive Bear</code>，全站 1,826 檔即刻重算。
                  </li>
                  <li>
                    <strong className="text-slate-100">自訂滑桿與回測：</strong>點擊左側「執行回測」或「ML工作室」，拖曳均線天期、乖離門檻或量比滑桿。
                  </li>
                  <li>
                    <strong className="text-slate-100">檢視回測計分板：</strong>即時觀察「重分類變動標的檔數」、年化報酬 (`CAGR`)、夏普比率 (`Sharpe`) 與最大回撤 (`Max DD`)，再點「執行回測並套用全市場」同步至全站。
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* Section 4: Core Indicators & 4G Formula Quick Reference */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            <div className="p-3 rounded-lg bg-[#0b0f17] border border-[#182232] space-y-1">
              <div className="flex items-center gap-1.5 font-mono font-bold text-sky-300">
                <Layers className="w-3.5 h-3.5" />
                <span>人與狗理論 (MA200 Bias)</span>
              </div>
              <p className="text-slate-300">
                主人為 <code className="text-sky-300">200日均線</code>，小狗為 <code className="text-sky-300">收盤價</code>。預設乖離 &gt;+35% 為 A3 狂熱賣區，&lt;-25% 為 B3 恐慌買區。
              </p>
            </div>

            <div className="p-3 rounded-lg bg-[#0b0f17] border border-[#182232] space-y-1">
              <div className="flex items-center gap-1.5 font-mono font-bold text-sky-300">
                <Layers className="w-3.5 h-3.5" />
                <span>2×2 = 5-1 估值與價差</span>
              </div>
              <p className="text-slate-300">
                定位股價於 200 日箱型階梯（階梯 -1 恐慌底 ～ 階梯 5 狂熱頂），結合台指期正逆價差判讀籌碼。
              </p>
            </div>

            <div className="p-3 rounded-lg bg-[#0b0f17] border border-[#182232] space-y-1">
              <div className="flex items-center gap-1.5 font-mono font-bold text-sky-300">
                <ShieldAlert className="w-3.5 h-3.5" />
                <span>4G 1% 風控部位公式</span>
              </div>
              <p className="text-slate-300">
                單筆最大風險 = <code className="text-sky-300">總資金 × 1% × 宏觀乘數</code>；停損距離取 <code className="text-sky-300">2×ATR14</code> 或 <code className="text-sky-300">20日低點</code>。
              </p>
            </div>

            <div className="p-3 rounded-lg bg-[#0b0f17] border border-[#182232] space-y-1">
              <div className="flex items-center gap-1.5 font-mono font-bold text-sky-300">
                <Cpu className="w-3.5 h-3.5" />
                <span>VR60 量比 &amp; RS20 強弱</span>
              </div>
              <p className="text-slate-300">
                <code className="text-sky-300">VR60 &lt; 0.8x</code> 為量縮築底，<code className="text-sky-300">&gt; 1.2x</code> 為放量突破；<code className="text-sky-300">RS20 &gt; 0</code> 代表跑贏大盤。
              </p>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#182232] text-[11px] font-mono text-slate-400">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-terminal-cyan" />
              <span>涵蓋範圍：TWSE 上市 + TPEx 上櫃共 1,826 檔全市場標的 | 完整教學請見 docs/user_guide.md</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 rounded-lg bg-primary-container hover:bg-blue-600 text-white font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <span>開始使用終端機</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
